import { useState, useCallback, useMemo } from "react";
import {
  Box,
  Stack,
  Text,
  Group,
  Badge,
  Card,
  ActionIcon,
  Tooltip,
  Checkbox,
  Button,
  Divider,
  Loader,
  Center,
  Modal,
} from "@mantine/core";
import {
  IconFileText,
  IconPhoto,
  IconDownload,
  IconTrash,
  IconEdit,
  IconCamera,
  IconEye,
  IconUpload,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { casesService, type CaseFile } from "../../services/cases.service";
import { caseImagesService, type CaseImage } from "../../services";
import { DocumentUpload } from "../accessioning";
import ImageUploadModal from "../cases/ImageUploadModal";
import { CaseFileEditModal } from "../cases/CaseFileEditModal";
import { CaseFileReportControls } from "./CaseFileReportControls";
import { CaseImageReportControls } from "./CaseImageReportControls";
import { PendingReportAttachmentNotice } from "./PendingReportAttachmentNotice";
import { getUploadSourceBadge } from "../../utils/uploadSource";
import type { UploadedFile } from "../../types";
import { isImageMimeType } from "../../utils/reportAttachments";
import { downloadFileFromUrl } from "../../utils/downloadFile";

interface DocumentsImagesCenterProps {
  caseId: string | null;
  isReadOnly?: boolean;
  isFinalized?: boolean;
  allowSignedPhotoAddition?: boolean;
  savedCaseFiles: CaseFile[];
  caseFilesLoading: boolean;
  uploadedDocFiles: UploadedFile[];
  onDocFilesChange: (files: UploadedFile[]) => void;
  onFileDeleted: (fileId: string) => void;
  onFileIncludeInReportToggled: (fileId: string, value: boolean) => void;
  onFileIncludeInReportChange: (fileId: string, value: boolean) => void;
  onFileUpdated?: (file: CaseFile) => void;
  onFileCaptionChange: (fileId: string, caption: string) => void;
  onImageCaptionChange: (imageId: string, caption: string) => void;
  onImageIncludeInReportChange: (imageId: string, include: boolean) => void;
  caseImages: CaseImage[];
  caseImagesLoading: boolean;
  imageDownloadUrls: Record<string, string>;
  onDownloadFile: (file: CaseFile) => void;
  onImagesReload: () => void;
  onOpenImageEdit: (img: CaseImage) => void;
  onDeleteImage: (imageId: string) => void;
  onOpenCameraCapture: () => void;
  pathologistReviewServiceGetPresignedUrl: (
    fileName: string,
    contentType: string
  ) => Promise<{ url: string; key: string }>;
}

export function DocumentsImagesCenter({
  caseId,
  isReadOnly,
  isFinalized = false,
  allowSignedPhotoAddition = false,
  savedCaseFiles,
  caseFilesLoading,
  uploadedDocFiles,
  onDocFilesChange,
  onFileDeleted,
  onFileIncludeInReportToggled,
  onFileIncludeInReportChange,
  onFileUpdated,
  onFileCaptionChange,
  onImageCaptionChange,
  onImageIncludeInReportChange,
  caseImages,
  caseImagesLoading,
  imageDownloadUrls,
  onDownloadFile,
  onImagesReload,
  onOpenImageEdit,
  onDeleteImage,
  onOpenCameraCapture,
  pathologistReviewServiceGetPresignedUrl,
}: DocumentsImagesCenterProps) {
  const canAddPhotos = !isReadOnly || allowSignedPhotoAddition;
  const documentFiles = useMemo(
    () => savedCaseFiles.filter((file) => !isImageMimeType(file.mimeType)),
    [savedCaseFiles]
  );
  const imageFiles = useMemo(
    () => savedCaseFiles.filter((file) => isImageMimeType(file.mimeType)),
    [savedCaseFiles]
  );
  const [deleteDocFileId, setDeleteDocFileId] = useState<string | null>(null);
  const [imageUploadModalOpened, setImageUploadModalOpened] = useState(false);
  const [togglingFileId, setTogglingFileId] = useState<string | null>(null);
  const [editingFile, setEditingFile] = useState<CaseFile | null>(null);

  const handleOpenFileEdit = useCallback((file: CaseFile) => {
    setEditingFile(file);
  }, []);
  const handleCloseFileEdit = useCallback(() => {
    setEditingFile(null);
  }, []);
  const handleFileEditSaved = useCallback(
    (updated: CaseFile) => {
      onFileUpdated?.(updated);
    },
    [onFileUpdated]
  );

  const handlePreviewDocument = useCallback(async (file: CaseFile) => {
    const childWindow = window.open(
      "",
      file.name,
      "width=600,height=500,resizable=yes,scrollbars=yes"
    );
    try {
      const result = await caseImagesService.getDownloadUrl(
        file.url,
        file.name
      );
      if (childWindow) {
        childWindow.location.href = result.url;
      }
    } catch {
      if (childWindow) childWindow.close();
      notifications.show({
        title: "Error",
        message: "Failed to open document preview",
        color: "red",
      });
    }
  }, []);

  const handlePreviewImage = useCallback(async (img: CaseImage) => {
    const childWindow = window.open(
      "",
      img.fileName || "imagePreview",
      "width=600,height=500,resizable=yes,scrollbars=yes"
    );
    try {
      const result = await caseImagesService.getDownloadUrl(
        img.imageUrl,
        img.fileName
      );
      if (childWindow) {
        childWindow.location.href = result.url;
      }
    } catch {
      if (childWindow) childWindow.close();
      notifications.show({
        title: "Error",
        message: "Failed to open image preview",
        color: "red",
      });
    }
  }, []);

  const handleDownloadImage = useCallback(async (img: CaseImage) => {
    try {
      if (!img.fileName) throw new Error("The image name is unavailable.");
      const result = await caseImagesService.getDownloadUrl(
        img.imageUrl,
        img.fileName
      );
      await downloadFileFromUrl(result.url, img.fileName);
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to download image",
        color: "red",
      });
    }
  }, []);

  const handleToggleFileIncludeInReport = useCallback(
    async (fileId: string) => {
      if (!caseId) return;
      setTogglingFileId(fileId);
      try {
        const updated = await casesService.toggleFileIncludeInReport(
          caseId,
          fileId
        );
        onFileIncludeInReportToggled(fileId, updated.includeInReport);
      } catch {
        notifications.show({
          title: "Error",
          message: "Failed to update file",
          color: "red",
        });
      } finally {
        setTogglingFileId(null);
      }
    },
    [caseId, onFileIncludeInReportToggled]
  );

  const handleConfirmDeleteCaseFile = useCallback(async () => {
    if (!caseId || !deleteDocFileId) return;
    try {
      await casesService.deleteFile(caseId, deleteDocFileId);
      onFileDeleted(deleteDocFileId);
      setDeleteDocFileId(null);
      notifications.show({
        title: "Deleted",
        message: "Document removed",
        color: "green",
      });
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to delete document",
        color: "red",
      });
    }
  }, [caseId, deleteDocFileId, onFileDeleted]);

  const getFileBadgeColor = (mimeType: string) => {
    if (mimeType?.startsWith("image/")) return "cyan";
    if (mimeType === "application/pdf") return "red";
    return "gray";
  };

  const getFileTypeLabel = (mimeType: string, name: string) => {
    if (mimeType === "application/pdf") return "PDF";
    const ext = name?.split(".").pop()?.toUpperCase();
    return ext || mimeType?.split("/")[1]?.toUpperCase() || "FILE";
  };

  return (
    <Box p="md">
      <Box style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        {/* ===== DOCUMENTS SECTION ===== */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Stack gap="sm">
            <Group gap="xs">
              <IconFileText size={18} color="var(--mantine-color-green-6)" />
              <Text fw={600} size="sm">
                Documents
              </Text>
              {documentFiles.length > 0 && (
                <Badge size="sm" variant="light" color="green" radius="sm">
                  {documentFiles.length}
                </Badge>
              )}
            </Group>

            <DocumentUpload
              files={uploadedDocFiles}
              onFilesChange={onDocFilesChange}
              maxFiles={10}
              getPresignedUrl={pathologistReviewServiceGetPresignedUrl}
              hideFileList
              showDropZone
              disabled={Boolean(isReadOnly && !canAddPhotos)}
              dropZonePlaceholder="Drag and drop documents here, or click to select"
            />

            {caseFilesLoading ? (
              <Center py="xl">
                <Loader size="sm" />
              </Center>
            ) : documentFiles.length === 0 &&
              uploadedDocFiles.filter((f) => !f.s3Key && !f.uploadError)
                .length === 0 ? (
              <Box
                p="lg"
                style={{
                  border: "1px dashed var(--mantine-color-gray-3)",
                  borderRadius: 8,
                  textAlign: "center",
                }}
              >
                <IconFileText
                  size={28}
                  color="var(--mantine-color-gray-4)"
                  style={{ margin: "0 auto 8px" }}
                />
                <Text size="sm" c="dimmed">
                  No documents attached to this case
                </Text>
              </Box>
            ) : (
              <Stack gap="xs">
                {documentFiles.map((file) => (
                  <Card key={file.id} p="sm" withBorder radius="sm">
                    <Stack gap={8}>
                      <Group gap="sm" wrap="nowrap" align="center">
                        <Box
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            background: "var(--mantine-color-gray-1)",
                          }}
                        >
                          <IconFileText
                            size={18}
                            color="var(--mantine-color-green-6)"
                          />
                        </Box>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" fw={500} truncate="end">
                            {file.name}
                          </Text>
                          {file.description && (
                            <Text size="xs" c="dimmed" lineClamp={2}>
                              {file.description}
                            </Text>
                          )}
                          <Group gap={4} mt={2}>
                            {(() => {
                              const source = getUploadSourceBadge(
                                file.documentType
                              );
                              return (
                                <Badge
                                  size="xs"
                                  variant="light"
                                  color={source.color}
                                >
                                  {source.label}
                                </Badge>
                              );
                            })()}
                            <Badge
                              size="xs"
                              variant="outline"
                              color={getFileBadgeColor(file.mimeType)}
                            >
                              {getFileTypeLabel(file.mimeType, file.name)}
                            </Badge>
                          </Group>
                        </Box>
                        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                          {!isReadOnly && (
                            <Tooltip label="Edit">
                              <ActionIcon
                                size="sm"
                                variant="light"
                                color="blue"
                                onClick={() => handleOpenFileEdit(file)}
                              >
                                <IconEdit size={14} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          <Tooltip label="Preview in new window">
                            <ActionIcon
                              size="sm"
                              variant="light"
                              color="blue"
                              onClick={() => handlePreviewDocument(file)}
                            >
                              <IconEye size={14} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Download">
                            <ActionIcon
                              size="sm"
                              variant="light"
                              color="teal"
                              onClick={() => onDownloadFile(file)}
                            >
                              <IconDownload size={14} />
                            </ActionIcon>
                          </Tooltip>
                          {!isReadOnly &&
                            file.documentType !== "accessioning" && (
                              <Tooltip label="Delete">
                                <ActionIcon
                                  size="sm"
                                  variant="light"
                                  color="red"
                                  onClick={() => setDeleteDocFileId(file.id)}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                        </Group>
                      </Group>

                      {isImageMimeType(file.mimeType) ? (
                        <CaseFileReportControls
                          fileId={file.id}
                          caption={file.label || ""}
                          includeInReport={file.includeInReport}
                          readOnly={Boolean(
                            file.finalizedInReport || !canAddPhotos
                          )}
                          isFinalized={isFinalized}
                          finalizedInReport={file.finalizedInReport}
                          includeReadOnly={Boolean(
                            file.finalizedInReport ||
                              togglingFileId === file.id ||
                              !canAddPhotos
                          )}
                          onCaptionChange={onFileCaptionChange}
                          onIncludeInReportChange={onFileIncludeInReportChange}
                        />
                      ) : (
                        <Stack gap={4}>
                          <Checkbox
                            size="sm"
                            label="Include in report"
                            checked={file.includeInReport}
                            disabled={
                              file.finalizedInReport ||
                              togglingFileId === file.id
                            }
                            onChange={() =>
                              handleToggleFileIncludeInReport(file.id)
                            }
                            styles={{
                              label: { cursor: "pointer", fontSize: 12 },
                            }}
                          />
                          <PendingReportAttachmentNotice
                            isFinalized={isFinalized}
                            includeInReport={file.includeInReport}
                            finalizedInReport={file.finalizedInReport}
                          />
                        </Stack>
                      )}
                    </Stack>
                  </Card>
                ))}
                {uploadedDocFiles
                  .filter((f) => !f.s3Key && !f.uploadError)
                  .map((f) => {
                    const name = f.file?.name || f.fileName || "Uploading...";
                    return (
                      <Card key={name} p="sm" withBorder radius="sm">
                        <Group gap="sm" wrap="nowrap" align="center">
                          <Box
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 6,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              background: "var(--mantine-color-gray-1)",
                            }}
                          >
                            <Loader size="xs" />
                          </Box>
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={500} truncate="end">
                              {name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              Uploading...
                            </Text>
                          </Box>
                        </Group>
                      </Card>
                    );
                  })}
              </Stack>
            )}
          </Stack>
        </Box>

        {/* ===== VERTICAL DIVIDER ===== */}
        <Divider orientation="vertical" />

        {/* ===== IMAGES SECTION ===== */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Group gap="xs">
                <IconPhoto size={18} color="var(--mantine-color-blue-6)" />
                <Text fw={600} size="sm">
                  Images
                </Text>
                {caseImages.length + imageFiles.length > 0 && (
                  <Badge size="sm" variant="light" color="blue" radius="sm">
                    {caseImages.length + imageFiles.length}
                  </Badge>
                )}
              </Group>
              {canAddPhotos && (
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="light"
                    color="blue"
                    leftSection={<IconCamera size={13} />}
                    onClick={onOpenCameraCapture}
                  >
                    Capture
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    color="blue"
                    leftSection={<IconUpload size={13} />}
                    onClick={() => setImageUploadModalOpened(true)}
                  >
                    Upload Image
                  </Button>
                </Group>
              )}
            </Group>

            {imageFiles.length > 0 && (
              <Stack gap="xs">
                {imageFiles.map((file) => (
                  <Card key={file.id} p="sm" withBorder radius="sm">
                    <Stack gap={8}>
                      <Group gap="sm" wrap="nowrap" align="center">
                        <Box
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            background: "var(--mantine-color-gray-1)",
                          }}
                        >
                          <IconPhoto
                            size={18}
                            color="var(--mantine-color-blue-6)"
                          />
                        </Box>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" fw={500} truncate="end">
                            {file.name}
                          </Text>
                          {file.description && (
                            <Text size="xs" c="dimmed" lineClamp={2}>
                              {file.description}
                            </Text>
                          )}
                          <Group gap={4} mt={2}>
                            {(() => {
                              const source = getUploadSourceBadge(
                                file.documentType
                              );
                              return (
                                <Badge
                                  size="xs"
                                  variant="light"
                                  color={source.color}
                                >
                                  {source.label}
                                </Badge>
                              );
                            })()}
                            <Badge
                              size="xs"
                              variant="outline"
                              color={getFileBadgeColor(file.mimeType)}
                            >
                              {getFileTypeLabel(file.mimeType, file.name)}
                            </Badge>
                          </Group>
                        </Box>
                        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                          {!isReadOnly && (
                            <Tooltip label="Edit">
                              <ActionIcon
                                size="sm"
                                variant="light"
                                color="blue"
                                onClick={() => handleOpenFileEdit(file)}
                              >
                                <IconEdit size={14} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          <Tooltip label="Preview in new window">
                            <ActionIcon
                              size="sm"
                              variant="light"
                              color="blue"
                              onClick={() => handlePreviewDocument(file)}
                            >
                              <IconEye size={14} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Download">
                            <ActionIcon
                              size="sm"
                              variant="light"
                              color="teal"
                              onClick={() => onDownloadFile(file)}
                            >
                              <IconDownload size={14} />
                            </ActionIcon>
                          </Tooltip>
                          {!isReadOnly &&
                            file.documentType !== "accessioning" && (
                              <Tooltip label="Delete">
                                <ActionIcon
                                  size="sm"
                                  variant="light"
                                  color="red"
                                  onClick={() => setDeleteDocFileId(file.id)}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                        </Group>
                      </Group>

                      {isImageMimeType(file.mimeType) ? (
                        <CaseFileReportControls
                          fileId={file.id}
                          caption={file.label || ""}
                          includeInReport={file.includeInReport}
                          readOnly={Boolean(
                            file.finalizedInReport || !canAddPhotos
                          )}
                          isFinalized={isFinalized}
                          finalizedInReport={file.finalizedInReport}
                          includeReadOnly={Boolean(
                            file.finalizedInReport ||
                              togglingFileId === file.id ||
                              !canAddPhotos
                          )}
                          onCaptionChange={onFileCaptionChange}
                          onIncludeInReportChange={onFileIncludeInReportChange}
                        />
                      ) : (
                        <Stack gap={4}>
                          <Checkbox
                            size="sm"
                            label="Include in report"
                            checked={file.includeInReport}
                            disabled={
                              file.finalizedInReport ||
                              togglingFileId === file.id
                            }
                            onChange={() =>
                              handleToggleFileIncludeInReport(file.id)
                            }
                            styles={{
                              label: { cursor: "pointer", fontSize: 12 },
                            }}
                          />
                          <PendingReportAttachmentNotice
                            isFinalized={isFinalized}
                            includeInReport={file.includeInReport}
                            finalizedInReport={file.finalizedInReport}
                          />
                        </Stack>
                      )}
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}

            {caseImagesLoading ? (
              <Center py="xl">
                <Loader size="sm" />
              </Center>
            ) : caseImages.length === 0 && imageFiles.length === 0 ? (
              <Box
                p="lg"
                style={{
                  border: "1px dashed var(--mantine-color-gray-3)",
                  borderRadius: 8,
                  textAlign: "center",
                }}
              >
                <IconPhoto
                  size={28}
                  color="var(--mantine-color-gray-4)"
                  style={{ margin: "0 auto 8px" }}
                />
                <Text size="sm" c="dimmed">
                  No images attached to this case
                </Text>
              </Box>
            ) : (
              <Stack gap="xs">
                {caseImages.map((img, idx) => (
                  <Card key={img.id} p="sm" withBorder radius="sm">
                    <Stack gap={8}>
                      <Group gap="sm" wrap="nowrap" align="flex-start">
                        {imageDownloadUrls[img.id] ? (
                          <Box
                            style={{
                              width: 56,
                              height: 56,
                              borderRadius: 6,
                              overflow: "hidden",
                              flexShrink: 0,
                              border: "1px solid var(--mantine-color-gray-3)",
                              cursor: "pointer",
                            }}
                            component="a"
                            href={imageDownloadUrls[img.id]}
                            target="_blank"
                          >
                            <img
                              src={imageDownloadUrls[img.id]}
                              alt={img.caption || `Image ${idx + 1}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                        ) : (
                          <Box
                            style={{
                              width: 56,
                              height: 56,
                              borderRadius: 6,
                              flexShrink: 0,
                              backgroundColor: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <IconPhoto
                              size={22}
                              color="var(--mantine-color-gray-5)"
                            />
                          </Box>
                        )}

                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" fw={500} truncate="end">
                            {img.fileName || `Image ${idx + 1}`}
                          </Text>
                          {img.caption && (
                            <Text size="xs" c="dimmed" truncate="end">
                              {img.caption}
                            </Text>
                          )}
                          {img.description && (
                            <Text size="xs" c="dimmed" lineClamp={2}>
                              {img.description}
                            </Text>
                          )}
                          {img.magnification && (
                            <Badge
                              size="xs"
                              variant="outline"
                              color="gray"
                              mt={2}
                            >
                              {img.magnification}
                            </Badge>
                          )}
                        </Box>

                        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                          {!isReadOnly && (
                            <Tooltip label="Edit">
                              <ActionIcon
                                size="sm"
                                variant="light"
                                color="blue"
                                onClick={() => onOpenImageEdit(img)}
                              >
                                <IconEdit size={14} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          <Tooltip label="Preview in new window">
                            <ActionIcon
                              size="sm"
                              variant="light"
                              color="blue"
                              onClick={() => handlePreviewImage(img)}
                            >
                              <IconEye size={14} />
                            </ActionIcon>
                          </Tooltip>
                          {imageDownloadUrls[img.id] && (
                            <Tooltip label="Download">
                              <ActionIcon
                                size="sm"
                                variant="light"
                                color="teal"
                                onClick={() => handleDownloadImage(img)}
                              >
                                <IconDownload size={14} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          {!isReadOnly && (
                            <Tooltip label="Delete">
                              <ActionIcon
                                size="sm"
                                variant="light"
                                color="red"
                                onClick={() => onDeleteImage(img.id)}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Group>
                      </Group>

                      <CaseImageReportControls
                        imageId={img.id}
                        caption={img.caption || ""}
                        includeInReport={img.includeInReport}
                        readOnly={Boolean(
                          img.finalizedInReport || !canAddPhotos
                        )}
                        includeReadOnly={Boolean(
                          img.finalizedInReport || !canAddPhotos
                        )}
                        onCaptionChange={onImageCaptionChange}
                        onIncludeInReportChange={onImageIncludeInReportChange}
                      />
                      <PendingReportAttachmentNotice
                        isFinalized={isFinalized}
                        includeInReport={img.includeInReport}
                        finalizedInReport={Boolean(img.finalizedInReport)}
                        subject="photo"
                      />
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Delete document confirmation modal */}
      <Modal
        opened={!!deleteDocFileId}
        onClose={() => setDeleteDocFileId(null)}
        title="Delete Document"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to delete this document? This action cannot be
            undone.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setDeleteDocFileId(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleConfirmDeleteCaseFile}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Image upload modal */}
      {caseId && (
        <ImageUploadModal
          opened={imageUploadModalOpened}
          onClose={() => setImageUploadModalOpened(false)}
          caseId={caseId}
          onUploadSuccess={() => {
            setImageUploadModalOpened(false);
            onImagesReload();
          }}
        />
      )}

      {/* Edit file metadata modal */}
      {caseId && (
        <CaseFileEditModal
          opened={!!editingFile}
          onClose={handleCloseFileEdit}
          caseId={caseId}
          file={editingFile}
          onSaved={handleFileEditSaved}
        />
      )}
    </Box>
  );
}
